import { Box, Collapse, Flex, Grid, Text } from '@chakra-ui/react'
import { FC, useEffect, useMemo } from 'react'

import useKpiLogistic from '@hook/kpi/use-logistic'
import useFilters from '@hook/shared/use-filters'
import useLayout from '@hook/shared/use-layout'
import useSessions from '@hook/shared/use-sessions'

import Layout from '@component/Layout'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'
import TableGrid from '@component/Layout/TableGrid'
import LayoutTools from '@component/Layout/Tools'
import KpiLogisticGraphic from '@component/Modules/Kpi/Graphics/KpiLogisticGraphic'
import OpenOrCloseChartButton from '@component/Modules/Kpi/Graphics/OpenOrCloseButton'
import KpiModal from '@component/Modules/Kpi/Modal'

import { disableActionsByDefault } from '@helper/kpi/logistic/disableActionsByDefault'
import { disableActionsByDefaultModal } from '@helper/kpi/logistic/disableActionsByDefaultModal'
import { scalesHeightKpiTable } from '@helper/kpi/logistic/scalesHeightKpiTable'
import { validateAppVersion } from '@helper/util/storangeClear'
import generic from '@service/generic'
const KpiLogistic: FC = () => {
  const { hasSessions } = useSessions()
  const { getFilters } = useFilters()
  const { menuToggle } = useLayout()
  const {
    data,
    query,
    params,
    headers,
    actions,
    loading,
    filters,
    fetching,
    filename,
    filtersList,
    currentyDate,
    tableInteraction,
    getData,
    setQuery,
    setFilters,
    setFilename,
    exportKpiData,
    clearAllFilters,
    handleUpdateParams,

    pagination,
    handlePageChange,

    dataModal,
    isModalOpen,
    loadingModal,
    headersModal,
    actionsModal,
    isModalConfig,
    isLoadingExport,
    kpiModalTableStructure,
    onModalClose,
    handleLocalParams,
    onModalChange,
    exportKpiDataModal,
    updateIsModalConfig,

    isGraphic,
    graphicValues,
    loadingGraphic,
    isShowContentChart,
    setIsGraphic,
    updateIsGraphic
  } = useKpiLogistic()

  const height = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 30px)'
          : '100vh'
        : hasSessions
        ? 'calc(100vh - 160px)'
        : 'calc(100vh - 142px)',
    [menuToggle, hasSessions]
  )

  const tableHeight = useMemo(
    () => scalesHeightKpiTable({ menuToggle, isGraphic, hasSessions }),
    [menuToggle, isGraphic, hasSessions]
  )

  useEffect(() => {
    generic
      .getMaintence('manutencao')
      .then(({ data: { data } }) => {
        validateAppVersion(data.version)

        if (data.valor === 'true') {
          window.location.href = '/manutencao'
        } else {
          handleLocalParams()

          data?.length === 0 && getData()

          getFilters({
            companies: true,
            centers: true,
            providersForDistribution: true,
            categoryManagers: true,
            productManagers: true,
            compactCategories: true,
            subCategories: true
          })
        }
      })
      .catch(() => {})

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout title="KPI Logística">
      <Grid
        w="100vw"
        bg="white"
        h={height}
        templateColumns={!filters ? '100vw' : '260px calc(100vw - 260px)'}
        isDisabled={loading || fetching || data.length === 0}
      >
        {filters && (
          <LayoutFilters
            filters={filtersList}
            defaultDate={currentyDate}
            values={params}
            onChange={handleUpdateParams}
            loading={loading}
            canCancel={fetching}
            onFilter={getData}
            onClearAllFilters={clearAllFilters}
          />
        )}

        <Box
          overflow={
            isShowContentChart && graphicValues.desvioMedio?.length > 0
              ? 'auto'
              : null
          }
          maxH={
            isShowContentChart && graphicValues.desvioMedio?.length > 0
              ? 'calc(100vh - 136px)'
              : null
          }
        >
          <LayoutTools
            title={filename}
            filters={filters}
            actions={actions}
            query={query}
            onClick={exportKpiData}
            onSearchChange={setQuery}
            onTitleChange={setFilename}
            toggleFilters={setFilters.toggle}
            isDisabled={loading || fetching || data.length === 0}
            disableActionsByDefault={disableActionsByDefault}
          />
          {loading ? (
            <LayoutLoader
              title="Recuperando informações..."
              message="Aguarde enquanto buscamos os registros no banco de dados"
            />
          ) : data?.length === 0 ? (
            <LayoutEmpty
              title="Nada encontrado!"
              message="Filtre sua busca ao lado para obter informações"
            />
          ) : (
            <>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                paddingRight={4}
                paddingLeft={3}
                paddingY={1}
              >
                <Text fontSize={14}>
                  Quantidade de Registros: {pagination.totalRows}
                </Text>

                {isShowContentChart &&
                  graphicValues.desvioMedio?.length > 0 && (
                    <OpenOrCloseChartButton
                      openText="Abrir gráfico"
                      closeText="Fechar gráfico"
                      graphicValue={graphicValues.desvioMedio}
                      isGraphic={isGraphic}
                      setIsGraphic={setIsGraphic}
                    />
                  )}
              </Flex>

              {isShowContentChart && (
                <Collapse
                  in={
                    loadingGraphic ||
                    (!loadingGraphic &&
                      graphicValues.desvioMedio?.length > 0 &&
                      isGraphic)
                  }
                  unmountOnExit={true}
                >
                  {loadingGraphic ? (
                    <LayoutLoader
                      type="logistic"
                      title="Recuperando informações..."
                      message="Aguarde enquanto buscamos os registros no banco de dados"
                    />
                  ) : graphicValues.desvioMedio?.length === 0 ? (
                    <LayoutEmpty
                      type="logistic"
                      title="Nada encontrado!"
                      message="Filtre sua busca ao lado para obter informações"
                    />
                  ) : (
                    isGraphic && (
                      <>
                        <KpiLogisticGraphic data={graphicValues} />
                        <Box
                          paddingBottom={3.5}
                          boxShadow="inset 0px 1px 5px 2px #dcdcdc"
                        />
                      </>
                    )
                  )}
                </Collapse>
              )}

              <TableGrid
                id="logistic-table"
                entity="kpi-logistic"
                data={data}
                query={query}
                headers={headers}
                loading={fetching}
                itsSortable={true}
                height={tableHeight}
                actions={tableInteraction}
                updateIsGraphic={updateIsGraphic}
                pagination={pagination}
                udatePageChange={handlePageChange}
                isPagedBack={true}
                isPaged={false}
                isOverflowDisabled={true}
              />

              <KpiModal
                id="logistic-table-modal"
                entity="kpi-logistic-modal"
                data={dataModal}
                isOpen={isModalOpen}
                onClose={onModalClose}
                headers={headersModal}
                query={query}
                onClickExport={exportKpiDataModal}
                onChange={onModalChange}
                itsSortable={true}
                loadingModal={loadingModal}
                actions={actionsModal}
                isModalConfig={isModalConfig}
                updateIsModalConfig={updateIsModalConfig}
                kpiModalTableStructure={kpiModalTableStructure}
                isDisabled={disableActionsByDefaultModal}
                isLoadingExport={isLoadingExport}
                isExport
                isPaged
              />
            </>
          )}
        </Box>
      </Grid>
    </Layout>
  )
}

export default KpiLogistic
